// PWA wiring for Thailand FloodSight: register the service worker, offer install, and surface a
// "มีรุ่นใหม่" bar when a newer frontend is deployed. The install/update bars auto-appear only for
// real visitors — automated runs (navigator.webdriver, i.e. the Playwright deploy gates) never see
// them, so they can never cover the 💧 / 🆘 / สรุป buttons the gates protect.
//
// CSP is script-src 'self'; this module is part of the hashed /assets bundle, so it needs no inline
// script. The SW itself lives at /sw.js (root scope) and the emergency /reset page is a static file.

import { createInstallController, type InstallController, type InstallGuidance } from "./install";
import { createUpdateController, type UpdateController, type UpdateState } from "./update";

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const VISIT_KEY = "fs.pwa.visited";
const auto = (): boolean => !navigator.webdriver; // suppress nags in automated sessions (deploy gates)

// Record this visit; return true only if the viewer has been here before. The install nudge waits
// for a return visit — polite for real users, and a hard guarantee for the deploy gates, which each
// run in a fresh browser context (first visit → nothing shown → it can never cover a button).
function returningVisitor(): boolean {
  try {
    const seen = !!localStorage.getItem(VISIT_KEY);
    if (!seen) localStorage.setItem(VISIT_KEY, "1");
    return seen;
  } catch {
    return false;
  }
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
}

export function initPwa(): void {
  const host = el("div", "pwa-host");
  document.body.appendChild(host);
  const returning = returningVisitor(); // records the visit now; gates its first-ever visit

  const canNudge = (): boolean => auto() && returning;

  /* ── iOS "เพิ่มไปยังหน้าจอโฮม" steps (a centred modal; only on tap, never auto) ───────── */
  let iosModal: HTMLElement | null = null;
  const showInstructions = (kind: InstallGuidance): void => {
    iosModal?.remove();
    const body = kind === "ios_safari"
      ? `<ol class="pwa-steps"><li>แตะปุ่ม <b>แชร์</b> <span aria-hidden="true">⬆️</span> ด้านล่างของ Safari</li>
         <li>เลือก <b>“เพิ่มไปยังหน้าจอโฮม”</b></li>
         <li>แตะ <b>“เพิ่ม”</b> มุมขวาบน</li></ol>`
      : `<p>เปิดเมนูของเบราว์เซอร์แล้วเลือก <b>“ติดตั้งแอป”</b> หรือ <b>“เพิ่มไปยังหน้าจอโฮม”</b>
         (บางเบราว์เซอร์จะมีไอคอนติดตั้งในแถบที่อยู่)</p>`;
    const sheet = el("div", "pwa-modal-card");
    sheet.innerHTML = `<div class="pwa-modal-head">📲 ติดตั้ง “น้ำถึงไหน”</div>${body}`;
    const close = el("button", "pwa-btn ghost", "เข้าใจแล้ว");
    sheet.appendChild(close);
    const modal = el("div", "pwa-modal");
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.appendChild(sheet);
    const dismiss = (): void => { modal.remove(); iosModal = null; };
    close.addEventListener("click", dismiss);
    modal.addEventListener("click", (e) => { if (e.target === modal) dismiss(); });
    document.addEventListener("keydown", function esc(e) { if (e.key === "Escape") { dismiss(); document.removeEventListener("keydown", esc); } });
    host.appendChild(modal);
    iosModal = modal;
    close.focus();
  };

  /* ── install bar (bottom) ─────────────────────────────────────────────────────────── */
  let installer: InstallController;
  let installBar: HTMLElement | null = null;
  const hideInstall = (): void => { installBar?.remove(); installBar = null; };
  const showInstall = (): void => {
    if (!canNudge() || installBar) return;
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const bar = el("div", "pwa-bar install");
    bar.setAttribute("role", "dialog");
    bar.setAttribute("aria-label", "ติดตั้งแอปน้ำถึงไหน");
    const msg = el("div", "pwa-bar-msg");
    msg.innerHTML = `<span class="pwa-bar-ico" aria-hidden="true">📲</span><span>ติดตั้ง <b>“น้ำถึงไหน”</b> เพื่อเปิดจากหน้าจอโฮม</span>`;
    const actions = el("div", "pwa-bar-actions");
    const primary = el("button", "pwa-btn", ios ? "วิธีติดตั้ง" : "ติดตั้ง");
    const later = el("button", "pwa-btn ghost", "ภายหลัง");
    primary.addEventListener("click", () => { void installer.install(); if (ios) hideInstall(); });
    later.addEventListener("click", () => installer.dismiss());
    actions.append(primary, later);
    bar.append(msg, actions);
    host.appendChild(bar);
    installBar = bar;
  };

  installer = createInstallController({
    onBanner: showInstall,
    onDismiss: hideInstall,
    onInstructions: showInstructions,
    onState: (s) => { if (s.state === "installed" || s.state === "install_accepted") hideInstall(); },
  });
  addEventListener("beforeinstallprompt", (e) => installer.beforeInstallPrompt(e));
  addEventListener("appinstalled", () => installer.appInstalled());
  installer.initial();

  /* ── update bar (bottom) ──────────────────────────────────────────────────────────── */
  let updateBar: HTMLElement | null = null;
  let updater: UpdateController;
  const hideUpdate = (): void => { updateBar?.remove(); updateBar = null; };
  const renderUpdate = (s: { state: UpdateState }): void => {
    if (s.state !== "update_available") { if (s.state !== "updating") hideUpdate(); return; }
    if (!auto() || updateBar) return;
    hideInstall(); // one bar at a time
    const bar = el("div", "pwa-bar update");
    bar.setAttribute("role", "dialog");
    bar.setAttribute("aria-label", "มีรุ่นใหม่");
    const msg = el("div", "pwa-bar-msg");
    msg.innerHTML = `<span class="pwa-bar-ico" aria-hidden="true">✨</span><span>มีรุ่นใหม่ของ <b>“น้ำถึงไหน”</b></span>`;
    const actions = el("div", "pwa-bar-actions");
    const now = el("button", "pwa-btn", "อัปเดตตอนนี้");
    const later = el("button", "pwa-btn ghost", "ภายหลัง");
    now.addEventListener("click", () => { now.disabled = true; updater.updateNow(); });
    later.addEventListener("click", hideUpdate);
    actions.append(now, later);
    bar.append(msg, actions);
    host.appendChild(bar);
    updateBar = bar;
  };
  updater = createUpdateController({
    reload: () => location.reload(),
    onState: renderUpdate,
    // The app has no unsaved composer; nothing to lose on reload. Left extensible on purpose.
  });

  /* ── service worker registration + update detection ──────────────────────────────── */
  if (!("serviceWorker" in navigator)) return;
  const hadController = !!navigator.serviceWorker.controller; // false on the very first install
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (hadController) updater.reloadOnce(); // reload once on a real update; never on first install
  });
  navigator.serviceWorker.register("/sw.js", { scope: "/" }).then((reg) => {
    updater.setRegistration(reg);
    let last = 0;
    let timer: ReturnType<typeof setInterval> | 0 = 0;
    const check = (): void => {
      if (document.hidden || Date.now() - last < 30000) return; // skip while hidden / within 30s
      last = Date.now();
      void updater.check();
    };
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) { if (timer) { clearInterval(timer); timer = 0; } }
      else { check(); if (!timer) timer = setInterval(check, CHECK_INTERVAL_MS); }
    });
    if (!document.hidden) timer = setInterval(check, CHECK_INTERVAL_MS);
    reg.addEventListener("updatefound", () => {
      const nw = reg.installing;
      if (!nw) return;
      nw.addEventListener("statechange", () => {
        if (nw.state === "installed" && navigator.serviceWorker.controller) updater.available(reg.waiting || nw);
      });
    });
    if (reg.waiting && hadController) updater.available(reg.waiting);
    check();
  }).catch(() => updater.fail("registration_failed"));
}
